/**
 * Helpers d'erreurs serveur — affichage uniformisé des erreurs API Zod
 * (`details.fieldErrors[<key>]`) dans les formulaires.
 *
 * Pourquoi ?
 * ----------
 * Avant 8.B, chaque page de form (`CompanyCreatePage`, `CompanyEditPage`)
 * réimplémentait :
 *   const apiError = err instanceof ApiException ? err.apiError : null;
 *   const fieldErrors = apiError?.details?.fieldErrors;
 *   const fieldError = (k) => fieldErrors?.[k]?.[0] ? <span...>...</span> : null;
 *
 * À 3 pages, c'est tolérable. À 6+ pages (8.B et au-delà), c'est une
 * dette UX garantie : le rendu d'erreur va diverger, certains forms
 * oublieront `key`, d'autres afficheront "[object Object]". On centralise.
 *
 * Sécurité
 * --------
 * Ces helpers ne loggent JAMAIS le payload de form qui a déclenché l'erreur.
 * On ne touche qu'à `error.code` et `error.message` (qui sont des chaînes
 * descriptives, pas des données utilisateur). Si on ajoutait un jour un
 * `console.warn` ici, il faudrait scrubber explicitement.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiException, type ApiError } from "./api";
import { getApiErrorMessage, normalizeLang } from "./apiErrorMessage";

export type FieldErrors = Record<string, string[]>;

/**
 * Extrait `apiError` + `fieldErrors` d'une erreur de mutation TanStack
 * Query. Retourne `{ apiError: null, fieldErrors: undefined }` si l'erreur
 * n'est pas une `ApiException` (erreur réseau, parsing, etc.).
 */
export function extractApiError(error: unknown): {
  apiError: ApiError | null;
  fieldErrors: FieldErrors | undefined;
} {
  if (!(error instanceof ApiException)) {
    return { apiError: null, fieldErrors: undefined };
  }
  const apiError = error.apiError;
  const fieldErrors =
    apiError.details && typeof apiError.details === "object"
      ? ((apiError.details as { fieldErrors?: FieldErrors }).fieldErrors ?? undefined)
      : undefined;
  return { apiError, fieldErrors };
}

/**
 * Helper d'affichage d'erreur de champ — retourne un node JSX ou null.
 * Usage type :
 *
 *   const { fieldErrors } = extractApiError(mutation.error);
 *   <input ... />
 *   <FieldError errors={fieldErrors} name="legal_name" />
 *
 * On affiche uniquement le PREMIER message d'erreur par champ (Zod en peut
 * cumuler plusieurs ; les autres sont rarement utiles à l'utilisateur).
 */
export function FieldError({
  errors,
  name,
}: {
  errors: FieldErrors | undefined;
  name: string;
}): ReactNode {
  const msg = errors?.[name]?.[0];
  if (!msg) return null;
  return (
    <span className="mt-1 block text-xs text-red-600" data-field-error={name}>
      {msg}
    </span>
  );
}

/**
 * Bandeau d'erreur global — à afficher en haut du form quand l'API renvoie
 * une erreur SANS fieldErrors structurés (ex. 500, 503, conflict métier).
 *
 * Si `fieldErrors` est défini, on suppose que les <FieldError> inline
 * couvrent l'affichage et on ne montre PAS le bandeau (pour éviter le
 * doublon "Validation a échoué" + erreurs inline).
 */
export function FormErrorBanner({
  apiError,
  fieldErrors,
  error,
}: {
  apiError: ApiError | null;
  fieldErrors: FieldErrors | undefined;
  /**
   * Erreur source (`mutation.error` typiquement). Optionnel — si fourni,
   * `getApiErrorMessage()` est utilisé pour résoudre le wording bilingue
   * (9.K.14 D58.3). Sinon, fallback sur `apiError.message` legacy FR.
   */
  error?: unknown;
}): ReactNode {
  const { i18n } = useTranslation();
  if (!apiError || fieldErrors) return null;
  const lang = normalizeLang(i18n.resolvedLanguage);
  const text = error !== undefined ? getApiErrorMessage(error, lang) : apiError.message;
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {text || apiError.message}
    </div>
  );
}
